import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const LOOKBACK_DAYS = 30;

// Keys are the Resend campaign tags written by functions/metrics/emailCounters.js.
// Order runs oldest-lifecycle-stage first so the stack reads as a funnel.
// `tileLabel` is the short form — the panel sits in a half-width grid column on
// the dashboard, so four tiles across leaves very little room per label.
const EMAIL_TYPES = [
  { key: 'onboarding-welcome', label: 'Welcome', tileLabel: 'Welcome', color: '#1B6B4A' },
  { key: 'onboarding-setupFamily', label: 'Set up family', tileLabel: 'Set up', color: '#3BA676' },
  { key: 'onboarding-customize', label: 'Customize', tileLabel: 'Customize', color: '#F5C842' },
  { key: 'coparent-invite', label: 'Co-parent invite', tileLabel: 'Invite', color: '#F2886D' },
];

function utcDateKey(date) {
  return date.toISOString().slice(0, 10);
}

// Emails are bursty — plenty of days have no sends and therefore no doc. Build
// the full window so the x-axis stays evenly spaced and gaps read as zero
// rather than silently collapsing two distant days next to each other.
function buildWindow(docs) {
  const byDate = new Map((docs || []).map((d) => [d.id, d]));
  const rows = [];
  const cursor = new Date();
  cursor.setUTCDate(cursor.getUTCDate() - (LOOKBACK_DAYS - 1));
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    const key = utcDateKey(cursor);
    const byType = byDate.get(key)?.byType || {};
    const row = { date: key.slice(5) };
    for (const { key: type } of EMAIL_TYPES) row[type] = byType[type] || 0;
    rows.push(row);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return rows;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  const visible = payload.filter((p) => (p.value || 0) > 0);
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm space-y-1">
      <p className="font-semibold text-ink-800">{label}</p>
      <p className="text-xs text-ink-500 font-medium">Total: {total}</p>
      {visible.length === 0 ? (
        <p className="text-xs text-ink-400">No sends</p>
      ) : (
        visible.map((p) => (
          <p key={p.dataKey} className="text-ink-500 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span>{p.name}:</span>
            <span>{p.value?.toLocaleString() ?? 0}</span>
          </p>
        ))
      )}
    </div>
  );
};

// Local tile rather than StatCard — StatCard stamps a "today" badge on every
// value, which would be wrong for lifetime counts.
const TypeTile = ({ label, title, color, lifetime, window: windowCount }) => (
  <div className="card p-4 flex flex-col gap-1.5 min-w-0">
    <p className="text-xs font-semibold uppercase tracking-wide text-ink-400 flex items-center gap-2" title={title}>
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      {label}
    </p>
    <p className="font-display text-2xl font-semibold text-ink-900 leading-none">
      {lifetime.toLocaleString()}
    </p>
    <p className="text-xs text-ink-400">{windowCount.toLocaleString()} in last 30d</p>
  </div>
);

const EmailsSentPanel = ({ emailStats, emailTotals }) => {
  const data = useMemo(() => buildWindow(emailStats), [emailStats]);

  const windowByType = useMemo(() => {
    const acc = {};
    for (const { key } of EMAIL_TYPES) {
      acc[key] = data.reduce((sum, row) => sum + (row[key] || 0), 0);
    }
    return acc;
  }, [data]);

  const windowTotal = useMemo(
    () => Object.values(windowByType).reduce((sum, n) => sum + n, 0),
    [windowByType]
  );

  const lifetimeTotal = emailTotals?.total || 0;
  const hasAnyData = lifetimeTotal > 0 || windowTotal > 0;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">Emails sent by type</h3>
        <span className="text-xs text-ink-400">
          Last 30 days · {windowTotal.toLocaleString()} sent
        </span>
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Counted at send time for every outbound email. Sends, not deliveries — a message
        can still bounce after Resend accepts it.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {EMAIL_TYPES.map(({ key, label, tileLabel, color }) => (
          <TypeTile
            key={key}
            label={tileLabel}
            title={label}
            color={color}
            lifetime={emailTotals?.byType?.[key] || 0}
            window={windowByType[key] || 0}
          />
        ))}
      </div>

      {hasAnyData ? (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d6" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#57534e' }}
              axisLine={{ stroke: '#e5e0d6' }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis tick={{ fontSize: 11, fill: '#57534e' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(27, 107, 74, 0.05)' }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {EMAIL_TYPES.map(({ key, label, color }, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={label}
                stackId="a"
                fill={color}
                radius={i === EMAIL_TYPES.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="border-2 border-dashed border-surface-200 rounded-xl p-8 text-center">
          <p className="text-sm text-ink-500 mb-1">No email sends counted yet.</p>
          <p className="text-xs text-ink-400">
            Counters start at the next send. Run “Backfill email stats” in Admin controls
            to pull in history from before they shipped.
          </p>
        </div>
      )}

      <p className="text-[11px] text-ink-400 mt-4 pt-3 border-t border-ink-100">
        Lifetime totals shown on the tiles; the chart covers the last 30 days.
      </p>
    </div>
  );
};

export default EmailsSentPanel;
